/* eslint-disable prefer-arrow/prefer-arrow-functions */
import {
  Dispatch,
  FormEvent,
  ForwardedRef,
  SetStateAction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { FileUploadHandle } from "../../../../../../models";
import { uploadListingFile } from "../../../../../../service/organisation";
import { ServiceError, Status } from "../../../../../../service/Shared";
import { useAuth } from "../../../../../../useAuth";
import { areObjectsDeepEqual, useIsLoadingWrapper } from "../../../../../../utils";
import { Toast } from "../../../../../../widget";
import {
  FormAboutUsData,
  FormNames,
  MarketingAssetsFormChangedAction,
  MarketingAssetsSubmitHandler,
  UpdateListingType,
} from "../../models";

interface UseAboutUsFormReturnData {
  teamPhotoUploadRef: ForwardedRef<FileUploadHandle>;
  enabled: boolean;
  content: string | null;
  errors: ServiceError[] | undefined;
  teamImageUrl: string | null | undefined;
  isHandleSubmitLoading: boolean;
  setEnabled: Dispatch<SetStateAction<boolean>>;
  setContent: Dispatch<SetStateAction<string | null>>;
  handleSetTeamPhoto: (f: File) => Promise<void>;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
  handleCancel: () => void;
  hasPermission: (permission: string) => boolean;
}
export const useAboutUsForm = (
  objectUuid: string,
  objectType: string,
  objectKey: string,
  updateListing: UpdateListingType,
  aboutUsDefaultValues: FormAboutUsData,
  aboutUsOldDefaultValues: FormAboutUsData,
  listingRowVersion: number,
  setListingRowVersion: Dispatch<SetStateAction<number>>,
  dispatch: Dispatch<MarketingAssetsFormChangedAction>,
  forwardedRef: ForwardedRef<MarketingAssetsSubmitHandler>,
  getCurrentFormsData: (formWhichWasSaved: string) => string
): UseAboutUsFormReturnData => {
  const { hasPermission } = useAuth();

  const [submittedValues, setSubmittedValues] = useState<FormAboutUsData>(aboutUsOldDefaultValues);
  const teamPhotoUploadRef = useRef<FileUploadHandle>(null);

  const [enabled, setEnabled] = useState(aboutUsDefaultValues.enabled);
  const [content, setContent] = useState(aboutUsDefaultValues.content);
  const [teamPhotoUuid, setTeamPhotoUuid] = useState<string | null | undefined>(aboutUsDefaultValues.imageFileUuid);
  const [teamImageUrl, setTeamImageUrl] = useState(aboutUsDefaultValues.imageFileUrl ?? null);
  const [errors, setErrors] = useState<ServiceError[] | undefined>();
  const [isHandleSubmitLoading, setIsHandleSubmitLoading] = useState(false);

  useImperativeHandle(forwardedRef, () => ({
    getAboutUsData() {
      return {
        enabled,
        content,
        imageFileUuid: teamPhotoUuid,
        imageFileUrl: teamImageUrl,
      };
    },
    getAboutUsSubmittedData() {
      return {
        ...submittedValues,
      };
    },
    setAboutUsErrors(previewErrors: ServiceError[]) {
      setErrors(previewErrors);
    },
  }));

  const handleSetTeamPhoto = async (f: File): Promise<void> => {
    if (objectUuid) {
      setErrors([]);
      const fileRes = await uploadListingFile({
        objectUuid,
        objectType,
        file: f,
      });

      if (fileRes.status === Status.Success && fileRes.data?.organisationListingFileUuid) {
        Toast.success({ message: "File uploaded successfully" });
        setTeamPhotoUuid(fileRes.data?.organisationListingFileUuid);
        setTeamImageUrl(fileRes.data?.file.url);
      }

      if (fileRes.status === Status.Error && fileRes.errors) {
        setErrors(fileRes.errors);
      }
    }
  };

  const getCurrentFormData = useCallback((): FormAboutUsData => {
    return {
      enabled,
      imageFileUuid: teamPhotoUuid,
      content: content || null,
      imageFileUrl: teamImageUrl,
    };
  }, [enabled, teamPhotoUuid, content, teamImageUrl]);

  useEffect(() => {
    dispatch({
      type: "SET_ABOUT_US_FORM_CHANGED",
      value: !areObjectsDeepEqual(submittedValues, getCurrentFormData()),
    });
  }, [enabled, teamPhotoUuid, content, submittedValues, teamImageUrl]);

  const handleSubmit = useIsLoadingWrapper(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrors([]);

    if (objectUuid) {
      const newAboutUsValues = {
        enabled,
        imageFileUuid: teamPhotoUuid,
        content: content || null,
        imageFileUrl: teamImageUrl,
      };

      const detailsRes = await updateListing({
        [objectKey]: objectUuid,
        content: getCurrentFormsData(FormNames.AboutUs),
        rowVersion: listingRowVersion,
      });

      if (detailsRes.status === Status.Success && detailsRes.data) {
        Toast.success({ message: "About us details changed successfully" });
        setSubmittedValues({
          ...newAboutUsValues,
        });
        setListingRowVersion(detailsRes.data?.rowVersion);
      }

      if (detailsRes.status === Status.Error) {
        setErrors(detailsRes.errors);
      }
    }
  }, setIsHandleSubmitLoading);

  const handleCancel = (): void => {
    if (teamPhotoUploadRef.current) {
      teamPhotoUploadRef.current.clearInput();
      setTeamPhotoUuid(submittedValues.imageFileUuid);
      setTeamImageUrl(submittedValues.imageFileUrl ?? null);
    }
    setEnabled(submittedValues.enabled);
    setContent(submittedValues.content);
    setErrors([]);
  };

  return {
    teamPhotoUploadRef,
    enabled,
    content,
    errors,
    teamImageUrl,
    isHandleSubmitLoading,
    setEnabled,
    setContent,
    handleSetTeamPhoto,
    handleSubmit,
    handleCancel,
    hasPermission,
  };
};
